import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Building2, ChevronDown, Plus, Check } from 'lucide-react';
import { toast } from 'sonner';

interface Organization {
    id: string;
    name: string;
    slug: string;
    status: string;
    plan_name?: string;
}

interface OrganizationSwitcherProps {
    onOrganizationChange?: (orgId: string) => void;
}

export function OrganizationSwitcher({ onOrganizationChange }: OrganizationSwitcherProps) {
    const queryClient = useQueryClient();
    const [currentOrgId, setCurrentOrgId] = useState<string | null>(
        localStorage.getItem('current_organization_id')
    );

    // Fetch organizations
    const { data: orgsData, isLoading } = useQuery({
        queryKey: ['organizations'],
        queryFn: async () => {
            const res = await fetch('/api/organizations');
            if (!res.ok) throw new Error('Failed to fetch');
            return res.json();
        }
    });

    const organizations: Organization[] = orgsData?.data || [];
    const currentOrg = organizations.find(o => o.id === currentOrgId) || organizations[0];

    // Switch organization
    const handleSwitch = async (orgId: string) => {
        localStorage.setItem('current_organization_id', orgId);
        setCurrentOrgId(orgId);

        // Update user's current organization on server
        try {
            const token = localStorage.getItem('token');
            await fetch('/api/auth/switch-organization', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({ organizationId: orgId })
            });
        } catch (error) {
            console.error('Failed to update organization on server');
        }

        // Invalidate all queries to refetch with new org context
        queryClient.invalidateQueries();

        onOrganizationChange?.(orgId);
        toast.success('تم التبديل إلى ' + organizations.find(o => o.id === orgId)?.name);
    };

    if (isLoading) {
        return (
            <Button variant="ghost" className="gap-2" disabled>
                <Building2 className="h-4 w-4" />
                <span className="text-sm">جاري التحميل...</span>
            </Button>
        );
    }

    if (organizations.length === 0) {
        return null;
    }

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-2 max-w-[200px]">
                    <Building2 className="h-4 w-4 shrink-0" />
                    <span className="truncate text-sm font-medium">
                        {currentOrg?.name || 'اختر المؤسسة'}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[240px]" dir="rtl">
                <DropdownMenuLabel>المؤسسات</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {organizations.map((org) => (
                    <DropdownMenuItem
                        key={org.id}
                        onClick={() => handleSwitch(org.id)}
                        className="flex items-center justify-between cursor-pointer"
                    >
                        <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-500" />
                            <div>
                                <div className="font-medium">{org.name}</div>
                                {org.plan_name && (
                                    <div className="text-xs text-slate-500">{org.plan_name}</div>
                                )}
                            </div>
                        </div>
                        {org.id === currentOrgId && (
                            <Check className="h-4 w-4 text-emerald-500" />
                        )}
                    </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem className="text-blue-600 cursor-pointer">
                    <Plus className="h-4 w-4 ml-2" />
                    إنشاء مؤسسة جديدة
                </DropdownMenuItem>
            </DropdownMenuContent>
        </DropdownMenu>
    );
}

export default OrganizationSwitcher;
